import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getBatchEntryData } from '~/selectors/batch-entry.selectors';
import { getRatesForBatchEntriesToBuy } from '~/selectors/batch-entry.composite.selectors';
import {
  getDefaultParcel,
  getUserAddress,
  getUserReturnAddress,
  getUser,
} from '~/selectors/user.selectors';
import { ApplicationState } from '~/store';
import { getPaymentMethods } from '~/selectors/billing.selectors';
import { useInsuranceSettings } from '~/pages/Settings/Insurance/hook';
import { TrackedOrderDetails, TrackingReference } from './types';
import Order, { OrderSelection } from '~/types/Order';
import { BatchEntryExtended } from '~/types/OrderBatchEntry';
import Rate from '~/types/Rate';
import APIAddress from '~/types/APIAddress';
import ParcelTemplate from '~/types/ParcelTemplate';
import { InsuranceSettings } from '~/types/Insurance';
import { PaymentMethodType } from '~/types/PaymentMethods';
import Address from '~/types/Address';
import Parcel from '~/types/Parcel';
import { getOrdersData, getSelectedOrders } from '~/selectors/orders.selectors';
import { trackingHelper } from './index';
import { isInsuranceAvailableForCountries } from '~/utils/insurance';
import { CountryIso2 } from '~/constants/countries';
import { singleOrMultiSelectionShouldShowCustoms } from '~/pages/Orders/Blackbeard/Drawers/LabelPurchaseDrawer/utils';
import { getLabelSettings } from '~/selectors/label-settings.selectors';

export function useTrackedOrderDetailsAndUserData(selectedOrders: OrderSelection) {
  const batchEntryIds = getBatchEntryIds(selectedOrders);
  const ordersData = useSelector(getOrdersData);
  const batchEntries = useSelector(getBatchEntryData);
  const ratesForOrders = useSelector((state: ApplicationState) =>
    getRatesForBatchEntriesToBuy(state, { batchEntryIds }),
  );
  const defaultSenderAddress = useSelector(getUserAddress);
  const defaultReturnAddress = useSelector(getUserReturnAddress);
  const defaultParcelTemplate = useSelector(getDefaultParcel);
  const paymentMethods = useSelector(getPaymentMethods);
  const labelSettings = useSelector(getLabelSettings);
  const user = useSelector(getUser);
  const { getInsuranceSettings, insuranceSettings } = useInsuranceSettings();

  useEffect(() => {
    if (user) {
      getInsuranceSettings();
    }
  }, [user, getInsuranceSettings]);
  const trackedOrderDetailsByShipment = useMemo(() => {
    const { paymentType, stripeToken } = paymentMethods || {};
    return getTrackedOrderDetailsByShipmentFromOrders({
      selectedOrders,
      ordersData,
      batchEntries,
      ratesForOrders,
      defaultSenderAddress,
      defaultReturnAddress,
      defaultParcelTemplate,
      paymentType,
      stripeToken,
      insuranceSettings: insuranceSettings || undefined,
      labelSettings,
    });
  }, [
    ordersData,
    selectedOrders,
    batchEntries,
    ratesForOrders,
    defaultSenderAddress,
    defaultReturnAddress,
    defaultParcelTemplate,
    paymentMethods,
    insuranceSettings,
    labelSettings,
  ]);

  const userDetails = useMemo(() => {
    const { paymentType } = paymentMethods || {};
    return {
      defaultSenderAddress: defaultSenderAddress?.id,
      defaultReturnAddress: defaultReturnAddress?.id,
      defaultParcelTemplate: defaultParcelTemplate?.objectId,
      paymentType,
      defaultInsuranceSetings: insuranceSettings || undefined,
    };
  }, [
    defaultSenderAddress,
    defaultReturnAddress,
    defaultParcelTemplate,
    paymentMethods,
    insuranceSettings,
  ]);

  return { trackedOrderDetailsByShipment, userDetails };
}

export function getTrackedOrderDetailsByShipmentFromState(state: ApplicationState) {
  const selectedOrders = getSelectedOrders(state);
  const batchEntryIds = getBatchEntryIds(selectedOrders);
  const ordersData = getOrdersData(state);
  const batchEntries = getBatchEntryData(state);
  const ratesForOrders = getRatesForBatchEntriesToBuy(state, { batchEntryIds });
  const defaultSenderAddress = getUserAddress(state);
  const defaultReturnAddress = getUserReturnAddress(state);
  const defaultParcelTemplate = getDefaultParcel(state);
  const paymentMethods = getPaymentMethods(state);
  const labelSettings = getLabelSettings(state);
  const insuranceSettings = trackingHelper.userDetails?.defaultInsuranceSetings;
  const { paymentType, stripeToken } = paymentMethods || {};

  const trackedOrderDetailsByShipment = getTrackedOrderDetailsByShipmentFromOrders({
    selectedOrders,
    ordersData,
    batchEntries,
    ratesForOrders,
    defaultSenderAddress,
    defaultReturnAddress,
    defaultParcelTemplate,
    paymentType,
    stripeToken,
    insuranceSettings: insuranceSettings || undefined,
    labelSettings,
  });

  return { trackedOrderDetailsByShipment };
}

export function updateTrackedOrderDetailsByShipmentSelectedRate(
  trackedOrderDetailsByShipment: TrackedOrderDetailsByShipment,
  trackingReference: TrackingReference,
  rates: Rate[],
  batchEntryData: HashMap<number, BatchEntryExtended>,
): TrackedOrderDetailsByShipment {
  if (trackingReference.orderIds) {
    return trackingReference.orderIds.reduce(
      (old: TrackedOrderDetailsByShipment, orderId: string) => {
        if (!trackingReference.selectedOrders) {
          return old;
        }

        return updateSelectedRateForOrder({
          trackedOrderDetailsByShipment: old,
          selectedOrders: trackingReference.selectedOrders,
          orderId,
          rates,
          batchEntryData,
        });
      },
      trackedOrderDetailsByShipment,
    );
  }

  if (trackingReference.orderId && trackingReference.selectedOrders) {
    return updateSelectedRateForOrder({
      trackedOrderDetailsByShipment,
      selectedOrders: trackingReference.selectedOrders,
      orderId: trackingReference.orderId,
      rates,
      batchEntryData,
    });
  }

  return trackedOrderDetailsByShipment;
}

export function getTrackedOrderDetailsByShipmentFromOrders({
  selectedOrders,
  ordersData,
  batchEntries,
  ratesForOrders,
  defaultSenderAddress,
  defaultReturnAddress,
  defaultParcelTemplate,
  paymentType,
  stripeToken,
  insuranceSettings,
  labelSettings,
}: {
  selectedOrders: OrderSelection;
  ordersData: Record<string, Order>;
} & TrackedOrderDetailsByShipmentInput): TrackedOrderDetailsByShipment {
  const orderIds = Object.keys(selectedOrders);
  const hasReturnOption = calculateHasReturnOption(
    selectedOrders,
    ordersData,
    batchEntries,
    defaultSenderAddress,
  );
  return orderIds.reduce((acc: TrackedOrderDetailsByShipment, orderId: string) => {
    const batchEntryIds = selectedOrders[orderId];
    const trackedOrderDetailsByShipment = getTrackedOrderDetailsByShipment({
      batchEntryIds,
      batchEntries,
      ratesForOrders,
      defaultSenderAddress,
      defaultReturnAddress,
      defaultParcelTemplate,
      paymentType,
      stripeToken,
      hasReturnOption,
      insuranceSettings: insuranceSettings || undefined,
      labelSettings,
    });

    return {
      ...acc,
      ...trackedOrderDetailsByShipment,
    };
  }, {});
}

function getTrackedOrderDetailsByShipment({
  batchEntryIds,
  batchEntries,
  ratesForOrders,
  defaultSenderAddress,
  defaultReturnAddress,
  defaultParcelTemplate,
  paymentType,
  stripeToken,
  insuranceSettings,
  hasReturnOption,
  labelSettings,
}: {
  batchEntryIds: number[];
  hasReturnOption: boolean;
} & TrackedOrderDetailsByShipmentInput): TrackedOrderDetailsByShipment {
  const hasInsuranceOption = calculateHasInsuranceOption(batchEntryIds, batchEntries);
  return batchEntryIds.reduce((acc: TrackedOrderDetailsByShipment, batchEntryId: number) => {
    const trackedOrderDetails = getTrackedOrderDetails({
      batchEntryId,
      batchEntries,
      ratesForOrders,
      defaultSenderAddress,
      defaultReturnAddress,
      defaultParcelTemplate,
      paymentType,
      stripeToken,
      hasInsuranceOption,
      hasReturnOption,
      insuranceSettings: insuranceSettings || undefined,
      labelSettings,
    });

    if (trackedOrderDetails) {
      return {
        ...acc,
        [batchEntryId]: trackedOrderDetails,
      };
    }

    return acc;
  }, {});
}

export function getTrackedOrderDetails({
  batchEntryId,
  batchEntries,
  ratesForOrders,
  defaultSenderAddress,
  defaultReturnAddress,
  defaultParcelTemplate,
  paymentType,
  stripeToken,
  insuranceSettings,
  hasInsuranceOption,
  hasReturnOption,
  labelSettings,
}: {
  batchEntryId: number;
  hasInsuranceOption: boolean;
  hasReturnOption: boolean;
} & TrackedOrderDetailsByShipmentInput): TrackedOrderDetails | undefined {
  if (!batchEntries) {
    return;
  }

  const be = batchEntries[batchEntryId];
  if (!be) {
    return;
  }

  const parcelDefaultSet: boolean =
    !!be.parcel?.userParcelTemplate &&
    be.parcel.userParcelTemplate.objectId === defaultParcelTemplate?.objectId;
  const rate = ratesForOrders.find((r) => r.shipment === be.shipmentObjectId);
  const paymentMethodDefaultSet = paymentType === PaymentMethodType.CreditCard;
  const returnDefaultSet = labelSettings?.isAutoCreateReturnLabels;
  const serviceLevelDefault = labelSettings?.outboundServiceLevel;
  return {
    addressSender: be.addressFrom?.objectId,
    addressSenderDefaultSet: isAddressSame(be.addressFrom, defaultSenderAddress),
    addressReturn: be.addressReturn?.objectId,
    addressReturnDefaultSet: isAddressSame(be.addressReturn, defaultReturnAddress),
    addressRecipient: be.addressTo?.objectId,
    parcel: serializeParcel(be.parcel),
    parcelDefaultSet,
    parcelUserTemplate: be.parcel?.userParcelTemplate
      ? be.parcel?.userParcelTemplate.objectId
      : undefined,
    parcelCarrierTemplate: be.parcel?.carrierParcelTemplate
      ? be.parcel?.carrierParcelTemplate.token
      : undefined,
    hasReturnOption,
    hasInsuranceOption,
    signature: be.extras?.signatureConfirmation?.hasSignatureConfirmation,
    hasSignatureOption: true, // This is never optional
    carrier: rate?.provider,
    serviceLevel: rate?.servicelevelName,
    paymentMethodDefaultSet,
    paymentMethod: stripeToken,
    insuranceDefaultSet: insuranceSettings?.is_auto_apply,
    returnDefaultSet,
    serviceLevelDefault,
  };
}

export function updateSelectedRateForOrder({
  trackedOrderDetailsByShipment,
  selectedOrders,
  orderId,
  rates,
  batchEntryData,
}: {
  trackedOrderDetailsByShipment: TrackedOrderDetailsByShipment;
  selectedOrders: OrderSelection;
  orderId: string;
  rates: Rate[];
  batchEntryData: HashMap<number, BatchEntryExtended>;
}): TrackedOrderDetailsByShipment {
  const beIds = selectedOrders[orderId];
  return beIds.reduce((acc: TrackedOrderDetailsByShipment, beId: number) => {
    const existing = acc[beId];
    const be = batchEntryData[beId];
    if (!existing || !be) {
      return acc;
    }

    const rate = rates.find((rate) => rate.shipment === be.shipmentObjectId);
    const trackedOrderDetails = {
      ...existing,
      carrier: rate?.provider,
      serviceLevel: rate?.servicelevelName,
    };

    return {
      ...acc,
      [beId]: trackedOrderDetails,
    };
  }, trackedOrderDetailsByShipment);
}

export function getBatchEntryIds(ordersSelected: OrderSelection) {
  return Object.keys(ordersSelected).reduce(
    (acc: number[], orderId: string) => [...acc, ...ordersSelected[orderId]],
    <number[]>[],
  );
}

export function isAddressSame(address1: Maybe<Address>, address2: Maybe<APIAddress>) {
  return (
    address1?.zip === address2?.zip &&
    address1?.state === address2?.state &&
    address1?.city === address2?.city &&
    address1?.street1 === address2?.street1 &&
    address1?.street2 === address2?.street2 &&
    address1?.streetNumber === address2?.streetNumber &&
    address1?.company === address2?.company &&
    address1?.phone === address2?.phone &&
    address1?.name === address2?.name
  );
}

function serializeParcel(parcel: Parcel | null | undefined): string | undefined {
  if (!parcel) {
    return;
  }

  if (parcel.userParcelTemplate) {
    const template = parcel.userParcelTemplate;
    const weight = template.weight || parcel.weight;
    const weightUnit = template.weightUnit || parcel.weightUnit;
    return `${template.length}x${template.width}x${template.height} ${template.distanceUnit}, ${weight} ${weightUnit}`;
  }

  if (parcel.carrierParcelTemplate) {
    const template = parcel.carrierParcelTemplate;
    const weight = parcel.weight;
    const weightUnit = parcel.weightUnit;
    return `${template.length}x${template.width}x${template.height} ${template.distanceUnit}, ${weight} ${weightUnit}`;
  }

  return `${parcel.length}x${parcel.width}x${parcel.height} ${parcel.distanceUnit}, ${parcel.weight} ${parcel.weightUnit}`;
}

export const calculateHasReturnOption = (
  selectedOrders: OrderSelection,
  ordersData?: Record<string, Order>,
  batchEntries?: HashMap<number, BatchEntryExtended>,
  defaultSenderAddress?: APIAddress | null,
): boolean => {
  if (!ordersData || !batchEntries) {
    return false;
  }

  const hasMultipleCountries =
    Array.from(
      new Set(
        Object.keys(selectedOrders)
          .map((orderId) => ordersData[orderId]?.toAddress?.country?.iso2)
          .filter((countryCode): countryCode is CountryIso2 => !!countryCode),
      ),
    ).length > 1;

  const selBatchEntries = getBatchEntryIds(selectedOrders)
    .map((beId) => batchEntries[beId])
    .filter((be) => !!be);

  const orderRequiresCustoms = singleOrMultiSelectionShouldShowCustoms(
    selBatchEntries,
    ordersData,
    selectedOrders,
    defaultSenderAddress || undefined,
  );

  return !hasMultipleCountries && !orderRequiresCustoms;
};

export const calculateHasInsuranceOption = (
  batchEntryIds: number[],
  batchEntries?: HashMap<number, BatchEntryExtended>,
): boolean => {
  if (!batchEntries) {
    return false;
  }

  const countries = batchEntryIds
    .map((beId) => batchEntries[beId]?.addressFrom?.country?.iso2)
    .filter((country): country is CountryIso2 => !!country);

  return isInsuranceAvailableForCountries(countries);
};

type TrackedOrderDetailsByShipment = Record<number, TrackedOrderDetails>;
interface TrackedOrderDetailsByShipmentInput {
  batchEntries?: HashMap<number, BatchEntryExtended>;
  ratesForOrders: Rate[];
  defaultSenderAddress: Maybe<APIAddress>;
  defaultReturnAddress: Maybe<APIAddress>;
  defaultParcelTemplate: Maybe<ParcelTemplate>;
  paymentType?: string;
  stripeToken?: string;
  insuranceSettings?: InsuranceSettings;
  labelSettings?: ReturnType<typeof getLabelSettings>;
}
